// dependencies
import * as Sentry from "@sentry/react";
import React from "react";

// schema
import { AiSettingsSchema, type IAIAssistantsSettings } from "@performyard/models";

import { AdminContainer, AdminTitle } from "../../../../components/Admin";
//components
import Button from "../../../../components/Button";
import { Grid, GridCol } from "../../../../components/Grid";
import { Checkboxes } from "../../../../components/Inputs/SelectionOptions/Checkboxes";
import Toggle from "../../../../components/Inputs/ToggleSwitches/ThumbSwitch";
import PageTitle from "../../../../components/PageTitle";
import Spacer from "../../../../components/Spacer";
import Text from "../../../../components/Text";

// hooks
import { useAiSettings, useUpdateAiSettings } from "../../../../api/hooks";
import { getFeatureFlagQuery, useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";
import useForm from "../../../../hooks/useForm";
import useToast from "../../../../hooks/useToast";

import { companyAISettingsQuery, currentUserQuery } from "../../../../api/queries";
import { dataLoader } from "../../../../api/queries/dataLoader";

// utils
import { showError } from "../../../../utils/RequestReponse";

// pages
import ErrorPage from "../../../Error";

/** Data loader function */
export const aIMeetingWorkspaceInsightsSettingsLoader = () => {
	const queries = [companyAISettingsQuery, getFeatureFlagQuery];

	return dataLoader([[currentUserQuery], queries]);
};

/** Page content */
const AIMeetingWorkspaceInsightsSettings = () => {
	const { mutateAsync: updateAiSettings, isPending } = useUpdateAiSettings();
	const { data } = useAiSettings();
	// ai feature flag enabled
	const { value: aiFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);

	const toast = useToast();

	const [enabled, setEnabled] = React.useState(data?.meetingWorkspaceInsightsEnabled);
	const { state, updateForm, setNewDefaultValues, resetForm, hasUnsavedChanges } = useForm({
		schema: AiSettingsSchema.shape.meetingWorkspaceInsightsSettings,
		defaultValues: data?.meetingWorkspaceInsightsSettings,
	});

	// toggles behave differently in that changes are saved without having to click the save button
	const handleToggle = async (val: boolean) => {
		try {
			await updateAiSettings({ meetingWorkspaceInsightsEnabled: val });
			setEnabled(val);
			toast(`Meeting Workspace Insights Assistant ${val === true ? "Enabled" : "Disabled"}.`, {
				type: "success",
				size: "large",
			});
		} catch {
			showError("Unable to save settings.");
		}
	};

	const discardChanges = () => {
		resetForm();
	};

	const handleSave = async () => {
		try {
			updateAiSettings({ meetingWorkspaceInsightsSettings: state });
			setNewDefaultValues(state);
			toast("Your changes have been saved.", { type: "success", size: "large" });
		} catch {
			showError("Unable to save settings.");
		}
	};

	const checkBoxOptions = [
		{ value: "progressAndGrowth", label: "Progress & Growth" },
		{ value: "keyAccomplishmentsAndWins", label: "Key Accomplishments & Wins" },
		{ value: "developmentAreasAndGrowthOpportunities", label: "Development Areas & Growth Opportunities" },
		{ value: "notableDiscussionsAndDecisions", label: "Notable Discussions & Decisions" },
		{ value: "actionItemsAnalysis", label: "Action Item Analysis" },
	];
	const checkBoxValues = checkBoxOptions.filter((option) => state?.[option.value]);
	const handleCheckBoxChange = (newOptions: { label: string; value: string }[]) => {
		// If the option is in newOptions, it is true, else false
		const newSettings = checkBoxOptions.reduce((acc, curr) => {
			acc[curr.value] = newOptions.some((option) => option.value === curr.value);
			return acc;
		}, {});
		updateForm({
			...(newSettings as IAIAssistantsSettings["meetingWorkspaceInsightsSettings"]),
		});
	};

	if (!aiFeatureFlagEnabled) {
		return <ErrorPage code={404} />;
	}

	return (
		<Sentry.ErrorBoundary fallback={undefined}>
			<Spacer height={4} />
			<AdminContainer>
				<PageTitle title="AI Meeting Workspace Insights Settings" />
				<AdminTitle>AI Meeting Workspace Insights</AdminTitle>
				<Spacer height={4} />
				<Grid cols={10} gap={2}>
					{/* left side of the page */}
					<GridCol span={4}>
						<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
							Meeting Workspace Insights Assistant
						</Text>
						<Spacer height={1} />
						<div className="max-w-[365px]">
							<Text size="paragraph-3" weight="regular" color="neutral-9">
								The Meeting Workspace Insights Assistant allows participants and admins to generate
								insights about all the meetings within a meeting workspace.
							</Text>
						</div>
						<Spacer height={3} />
					</GridCol>
					<GridCol span={1} />

					{/* right side of the page */}
					<GridCol span={5}>
						<Grid cols={5}>
							{/* enable meeting workspace insights assistant toggle */}
							<GridCol span={3}>
								<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
									Enable Meeting Workspace Insights Assistant
								</Text>
								<Spacer height={1} />
								<div className="max-w-[300px]">
									<Text size="paragraph-3" weight="regular" color="neutral-9">
										Disabling this setting will hide the ability for participants and admins to
										generate insights for a meeting workspace.
									</Text>
								</div>
							</GridCol>

							<GridCol span={2}>
								<div data-testid="enable-toggle" className="text-right">
									<div className="inline-block">
										<Toggle onChange={(val) => handleToggle(val)} value={enabled || false} />
									</div>
								</div>
							</GridCol>

							{/* Insights Content controls */}
							<GridCol span={5}>
								<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
									Insights Content
								</Text>
								<Spacer height={1} />
								<div>
									<Text size="paragraph-3" weight="regular" color="neutral-9">
										Control each section of content inside your meeting workspace insights, to only
										highlight content that is relevant to you and your company. These options are in
										addition to an overall summary of the meeting workspace.
									</Text>
								</div>
								<Spacer height={2} />
								<Checkboxes
									options={checkBoxOptions}
									value={checkBoxValues}
									onChange={handleCheckBoxChange}
									checkboxLabelWeight="semi-bold"
									className="!gap-4"
								/>
							</GridCol>
						</Grid>
					</GridCol>
				</Grid>
				<Spacer height={5} />
				<div className="flex justify-end gap-2">
					{hasUnsavedChanges ? (
						<span data-testid="discard-changes">
							<Button isHollow={true} onClick={discardChanges}>
								Discard
							</Button>
						</span>
					) : null}

					<span data-testid="save-changes">
						<Button
							isRequesting={isPending}
							color="green"
							onClick={handleSave}
							disabled={!hasUnsavedChanges}
						>
							Save Changes
						</Button>
					</span>
				</div>
			</AdminContainer>
		</Sentry.ErrorBoundary>
	);
};

export default AIMeetingWorkspaceInsightsSettings;
